import { Button } from "@jetblue/kit/button";
import { IcCloseBold } from "@jetblue/kit-icons/ic-close-bold";
import { SingleDatePicker } from "@jetblue/kit/date-picker";

import type { FlightSearchFormLabels } from "../labels";
import type { StatelessFlightSearchFormProperties } from "../stateless";
import type { useFlightSearchForm } from "../hooks/use-flight-search-form";
import type { PassengerCounts } from "../use-passengers";
import type { useForm } from "../use-form";
import { InputRoute } from "@jb-dotcom/dotcom-components/input-route";
import { FieldError } from "@jetblue/kit/field-error";

export type MultiCityProperties = {
  errors: ReturnType<typeof useFlightSearchForm>["errors"];
  formList: ReturnType<typeof useForm>;
  labels: FlightSearchFormLabels;
  odQuery: StatelessFlightSearchFormProperties["odQuery"];
  passengers: PassengerCounts;
};
export const MultiCity = ({
  errors,
  formList: {
    routes,
    swapOriginDestination,
    modify,
    minRoutes,
    remove,
    add,
    canAdd,
    startDate,
    endDate,
  },
  labels,
  odQuery,
}: MultiCityProperties) => (
  <div>
    {routes.map(({ destination, departureDate, origin }, index) => (
      <div className="not-first:mt-2" key={index}>
        <div className="mb-1 flex items-center justify-between">
          <h4 className="text-s-heading-extra-bold text-core-blue">
            {"Flight"} {index + 1}
          </h4>

          {index >= minRoutes && (
            <button
              className="flex items-center text-s-body-extra-bold text-royal-blue focus-outline hover:text-core-blue"
              data-fs-element={`remove-${index.toString()}`}
              onClick={() => {
                remove(index);
              }}
              type="button"
            >
              <IcCloseBold aria-hidden className="mr-1 size-2.5" />
              {labels.removeFlight}
            </button>
          )}
        </div>

        <div className="flex flex-col gap-1 md:flex-row">
          <InputRoute
            className="w-full md:w-2/3"
            data-fs-element={`route-${index.toString()}`}
            destination={destination}
            destinationName={`to[${index.toString()}]`}
            labels={labels}
            odRouteData={{ destination: odQuery.data, origin: odQuery.data }}
            onDestinationChange={(value) => {
              modify(index, "destination", value);
            }}
            onOriginChange={(value) => {
              modify(index, "origin", value);
            }}
            onSwapClick={() => {
              swapOriginDestination(index);
            }}
            origin={origin}
            originName={`from[${index.toString()}]`}
            showValidation={errors.showValidation}
          />

          <div
            className="flex w-full md:w-1/3"
            data-fs-element={`date-${index.toString()}`}
          >
            <SingleDatePicker
              className="w-full"
              endDate={endDate}
              error={
                <FieldError>
                  {({ isInvalid }) =>
                    isInvalid ? labels.errors.departureDate : ""
                  }
                </FieldError>
              }
              labels={{
                close: labels.close,
                done: labels.done,
                input: labels.departureDate,
                reset: labels.reset,
              }}
              selectedDate={departureDate}
              setSelectedDate={(date) => {
                modify(index, "departureDate", date);
              }}
              showValidation={errors.showValidation}
              // Use previous leg’s date as the min for legs 2+
              startDate={
                index > 0
                  ? (routes[index - 1]?.departureDate ?? startDate)
                  : startDate
              }
            />
          </div>
        </div>

        <input
          className="hidden"
          name={`travelDate[${index.toString()}]`}
          readOnly
          value={departureDate?.toString()}
        />
      </div>
    ))}

    <div className="relative flex flex-col justify-center md:flex-row">
      {canAdd && (
        <button
          className="mt-4 flex items-center text-s-body-extra-bold text-royal-blue focus-outline hover:text-core-blue md:absolute md:left-0"
          data-fs-element="add-flight"
          onClick={() => {
            add();
          }}
          type="button"
        >
          <IcCloseBold aria-hidden className="mr-1 size-2 rotate-45" />

          {labels.addFlight}
        </button>
      )}

      <Button
        className="mt-4 w-full md:w-unset"
        data-fs-element="submit"
        size="large"
        type="submit"
      >
        {labels.submit}
      </Button>
    </div>
  </div>
);
